import type { JSX, ReactNode } from "react";

import { ActionIcon, Box, Group, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import {
    hasShownClaimFields,
    sendReplacementClaimDisplayedMetric,
} from "../../../../shared-utils/sendReplacementClaimMetric";
import { useDeviceReplacement } from "../../../../stores";
import styles from "./DeviceInsights.module.css";
import { mapPartnerToDisplayName } from "./OffersView.helpers";

function DetailField({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
    return (
        <Stack gap="4xs">
            <Text size="xs" c="gray.5">
                {label}
            </Text>
            <Text size="sm" c="gray.1" className={className}>
                {children}
            </Text>
        </Stack>
    );
}

// Show only the last 4 digits; the full value is available via the reveal toggle.
export function maskImei(imei: string): string {
    if (imei.length <= 4) return imei;
    return `${"*".repeat(imei.length - 4)}${imei.slice(-4)}`;
}

function ImeiField({ imei }: { imei: string }): JSX.Element {
    const [revealed, setRevealed] = useState(false);
    const clipboard = useClipboard({ timeout: 1500 });
    const { analytics } = useSalesContext();

    const handleCopy = () => {
        clipboard.copy(imei);
        void analytics.dispatcher.dispatchBusinessEvent("IMEI_Copied");
    };

    const toggleReveal = () => {
        const next = !revealed;
        // Fire only when unmasking to the full value, not when re-masking.
        if (next) void analytics.dispatcher.dispatchBusinessEvent("IMEI_Revealed");
        setRevealed(next);
    };

    return (
        <Stack gap="4xs">
            <Text size="xs" c="gray.5">
                IMEI Collected
            </Text>
            <Group gap="xs" wrap="nowrap">
                <Tooltip label={clipboard.copied ? "Copied" : "Click to copy"} withArrow>
                    <UnstyledButton onClick={handleCopy} aria-label="Copy IMEI">
                        <Text size="sm" c="gray.1" className="fs-mask">
                            {revealed ? imei : maskImei(imei)}
                        </Text>
                    </UnstyledButton>
                </Tooltip>
                <ActionIcon
                    variant="subtle"
                    color="violet"
                    size="sm"
                    onClick={toggleReveal}
                    aria-label={revealed ? "Hide IMEI" : "Show IMEI"}
                >
                    {revealed ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                </ActionIcon>
            </Group>
        </Stack>
    );
}

function toSentenceCase(value?: string): string | null {
    if (!value) return null;
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase().replace(/_/g, " ");
}

function formatDateUTC(isoString?: string): string | null {
    if (!isoString) return null;
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { timeZone: "UTC" });
}

interface Props {
    showClaimDetails?: boolean;
}

export function DeviceInsights({ showClaimDetails = false }: Props): JSX.Element | null {
    const formatPhoneNumberDisplay = (phoneNumber: string): string => {
        if (phoneNumber.length <= 3) return `(${phoneNumber}`;
        if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };

    const { sessionId, ivrInfo, partner } = useSalesContext();
    const { productTitle, orderNumber, brandPhoneNumber, supportPhoneNumber, summary, device, accountId } =
        ivrInfo || {};

    const deviceReplacement = useDeviceReplacement(sessionId);
    const imei = deviceReplacement?.imei?.trim();

    const claimDate = formatDateUTC(deviceReplacement?.claimDate?.trim());
    const claimStatus = toSentenceCase(deviceReplacement?.serviceOrderStatus?.trim());
    const replacementDevice = [deviceReplacement?.make?.trim(), deviceReplacement?.model?.trim()]
        .filter(Boolean)
        .join(" ");

    const hasIvrDetails = Boolean(
        productTitle || orderNumber || brandPhoneNumber || supportPhoneNumber || summary || device || accountId,
    );
    const hasClaimFields = hasShownClaimFields({ imei, claimDate, status: claimStatus, replacementDevice });

    const showClaimSection = showClaimDetails && hasClaimFields;
    const showStandaloneImei = !showClaimDetails && !!imei;

    useEffect(() => {
        if (!showClaimSection) return;
        sendReplacementClaimDisplayedMetric(sessionId, partner, {
            imei,
            claimDate,
            status: claimStatus,
            replacementDevice,
        });
    }, [showClaimSection, imei, claimDate, claimStatus, replacementDevice, sessionId, partner]);

    if (!hasIvrDetails && !showClaimSection && !showStandaloneImei) return null;

    return (
        <Box
            data-testid="device-insights"
            bg="dark.5"
            m="sm"
            style={{
                borderRadius: "var(--mantine-radius-md)",
                maxWidth: 360,
            }}
        >
            <Stack p="sm">
                {productTitle ? <DetailField label="Product details">{productTitle}</DetailField> : null}

                {orderNumber ? (
                    <DetailField label="Order number" className="fs-mask">
                        {orderNumber}
                    </DetailField>
                ) : null}

                {brandPhoneNumber || supportPhoneNumber ? (
                    <Stack gap="4xs">
                        <Text size="xs" c="gray.5">
                            Support phone numbers
                        </Text>
                        <Stack gap="0">
                            {brandPhoneNumber ? (
                                <Text size="sm" c="gray.1">
                                    Brand/Manufacturer: {formatPhoneNumberDisplay(brandPhoneNumber)}
                                </Text>
                            ) : null}
                            {supportPhoneNumber ? (
                                <Text size="sm" c="gray.1">
                                    {mapPartnerToDisplayName(partner)}: {formatPhoneNumberDisplay(supportPhoneNumber)}
                                </Text>
                            ) : null}
                        </Stack>
                    </Stack>
                ) : null}

                {accountId ? (
                    <DetailField label="Account Number" className="fs-mask">
                        {accountId}
                    </DetailField>
                ) : null}

                {device ? <DetailField label="Device">{device}</DetailField> : null}

                {summary ? <DetailField label="Summary">{summary}</DetailField> : null}

                {showClaimSection ? (
                    <Box p="sm" className={styles.claimSection}>
                        <Stack gap="xs">
                            <Text size="xs" fw={600} c="gray.4">
                                Replacement claim
                            </Text>

                            {/* key={imei} remounts on a new value so reveal + "Copied" state never carry across IMEIs. */}
                            {imei ? <ImeiField key={imei} imei={imei} /> : null}

                            {claimDate ? <DetailField label="Claim date">{claimDate}</DetailField> : null}

                            {claimStatus ? <DetailField label="Status">{claimStatus}</DetailField> : null}

                            {replacementDevice ? (
                                <DetailField label="Replacement device">{replacementDevice}</DetailField>
                            ) : null}
                        </Stack>
                    </Box>
                ) : showStandaloneImei ? (
                    // key={imei} remounts on a new value so reveal + "Copied" state never carry across IMEIs.
                    <ImeiField key={imei} imei={imei} />
                ) : null}
            </Stack>
        </Box>
    );
}
